/**
 * bc-grid-view-sizes — keep product-card <img sizes> honest under Horizon's
 * shopper-facing grid-density toggle.
 *
 * snippets/card-gallery.liquid (bc-mod) ends a card's `sizes` with `50vw` on
 * mobile because the collection/search grid is 2-up there. Horizon also lets
 * the shopper switch that grid to one column ("single item" in
 * snippets/grid-density-controls.liquid → `product-grid-view="mobile-single"`
 * on the <ul>, persisted in sessionStorage and re-applied on every load by the
 * inline script in snippets/product-grid.liquid). In that view a card is
 * ~100vw wide, so a 50vw hint would under-fetch the eager images (lazy ones
 * carry `sizes="auto, …"` and size themselves).
 *
 * This watches every `[product-grid-view]` grid and rewrites the trailing
 * term of each card image's `sizes`: `100vw` while the view is mobile-single,
 * the server-emitted value otherwise (stashed on first touch). Attribute
 * flips (toggle) and new cards (infinite scroll / pagination morphs) are both
 * covered by one MutationObserver. Pure addition — no upstream JS modified.
 */
(() => {
  const ATTR = 'product-grid-view';
  const SEL = `[${ATTR}]`;
  const IMG = 'img.product-media__image[sizes]';

  const apply = (grid) => {
    const single = grid.getAttribute(ATTR) === 'mobile-single';
    grid.querySelectorAll(IMG).forEach((img) => {
      if (!img.dataset.bcSizes) img.dataset.bcSizes = img.getAttribute('sizes');
      const base = img.dataset.bcSizes;
      const next = single ? base.replace(/,\s*50vw\s*$/, ', 100vw') : base;
      if (img.getAttribute('sizes') !== next) img.setAttribute('sizes', next);
    });
  };

  const run = () => document.querySelectorAll(SEL).forEach(apply);

  const observer = new MutationObserver((records) => {
    const grids = new Set();
    for (const r of records) {
      if (r.type === 'attributes') {
        if (r.target.matches(SEL)) grids.add(r.target);
      } else {
        const grid = r.target.closest ? r.target.closest(SEL) : null;
        if (grid && grid.getAttribute(ATTR) === 'mobile-single') grids.add(grid);
      }
    }
    grids.forEach(apply);
  });

  const start = () => {
    run();
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: [ATTR],
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
